import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  listB2bike,
  listB2bikeProducers,
  type B2bikeListItem,
} from "../lib/api.js";

/**
 * Katalog B2Bike — seznam s filtrem podle výrobce + search + sklad.
 */
export function B2bikePage(): JSX.Element {
  const [items, setItems] = useState<B2bikeListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [producer, setProducer] = useState<number | undefined>(undefined);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 30;
  const [producers, setProducers] = useState<
    Array<{ id: number | null; name: string | null; count: number }>
  >([]);

  useEffect(() => {
    listB2bikeProducers()
      .then((res) => setProducers(res.items))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params: Parameters<typeof listB2bike>[0] = {
      search,
      inStock: inStockOnly,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
    if (producer !== undefined) params.producer = producer;
    listB2bike(params)
      .then((res) => {
        if (!alive) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [search, producer, inStockOnly, page]);

  const totalPages = useMemo(() => Math.ceil(total / PAGE_SIZE), [total]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900">B2Bike</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Velkoobchodní katalog (Spidi, Cardo, Garmin, Hepco Becker…) —{" "}
          <strong>{total.toLocaleString("cs-CZ")}</strong> produktů.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <input
            type="text"
            placeholder="Hledat: kód, název, výrobce…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setSearch(searchInput.trim());
                setPage(0);
              }
            }}
            className="w-full px-3.5 py-2 border border-zinc-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ktm-orange focus:border-transparent text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setSearch(searchInput.trim());
            setPage(0);
          }}
          className="px-4 py-2 bg-ktm-orange hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition"
        >
          Hledat
        </button>
        <select
          value={producer ?? ""}
          onChange={(e) => {
            setProducer(e.target.value ? Number(e.target.value) : undefined);
            setPage(0);
          }}
          className="px-3 py-2 border border-zinc-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-ktm-orange"
        >
          <option value="">Všichni výrobci</option>
          {producers.map((p) => (
            <option key={p.id} value={p.id ?? ""}>
              {p.name} ({p.count})
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={inStockOnly}
            onChange={(e) => {
              setInStockOnly(e.target.checked);
              setPage(0);
            }}
            className="rounded text-ktm-orange focus:ring-ktm-orange"
          />
          Skladem
        </label>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4">
          {error}
        </div>
      )}

      {loading && !items && (
        <div className="text-sm text-zinc-500">Načítám…</div>
      )}

      {items && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((p) => (
            <ProductCard key={p.id} p={p} />
          ))}
        </div>
      )}

      {items?.length === 0 && !loading && (
        <div className="text-center py-12 text-zinc-400 text-sm">Nic nenalezeno.</div>
      )}

      {total > PAGE_SIZE && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-xs text-zinc-500">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} z{" "}
            {total.toLocaleString("cs-CZ")}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 text-sm border border-zinc-300 rounded-lg disabled:opacity-40 hover:bg-zinc-50"
            >
              ← Předchozí
            </button>
            <span className="text-xs text-zinc-500 px-2">
              {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 text-sm border border-zinc-300 rounded-lg disabled:opacity-40 hover:bg-zinc-50"
            >
              Další →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductCard({ p }: { p: B2bikeListItem }): JSX.Element {
  return (
    <Link
      to={`/b2bike/${p.id}`}
      className="bg-white rounded-xl ring-1 ring-zinc-200 shadow-sm overflow-hidden hover:shadow-md hover:ring-zinc-300 transition flex flex-col"
    >
      <div className="aspect-square bg-zinc-50 flex items-center justify-center overflow-hidden">
        {p.imageUrl ? (
          <img
            src={p.imageUrl}
            alt={p.name ?? p.code}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        ) : (
          <div className="text-zinc-300 text-xs">bez obrázku</div>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        {p.producer && (
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400 mb-0.5">
            {p.producer}
          </div>
        )}
        <h3 className="text-sm font-medium text-zinc-900 line-clamp-2 mb-2">
          {p.name ?? p.code}
        </h3>
        <div className="mt-auto flex items-end justify-between gap-2">
          <div>
            {p.priceGross && (
              <div className="text-base font-semibold text-zinc-900">
                {Number(p.priceGross).toLocaleString("cs-CZ", { maximumFractionDigits: 2 })}{" "}
                {p.currency}
              </div>
            )}
            <div className="text-[10px] text-zinc-400 font-mono">{p.code}</div>
          </div>
          {p.variantsInStock > 0 && (
            <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
              Skladem
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
